import { Phone, Check, Calendar, MapPin, Clock, ArrowRight } from 'lucide-react';

const WHATSAPP_LINK = "https://wa.me/5541988451399?text=Olá,%20gostaria%20de%20agendar%20uma%20avaliação%20médica.";

export const AgendamentoPage = () => {
  return (
    <main data-testid="agendamento-page" className="pt-24">
      {/* Hero */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-sm uppercase tracking-[0.2em] text-[#B8944D] mb-6">
              Dr. Eduardo Hadas — CRM-PR 40929
            </p>
            
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O primeiro passo para um tratamento médico sério{' '}
              <span className="text-[#B8944D]">começa aqui</span>.
            </h1>
            
            <p className="text-lg text-[#666666] leading-relaxed mb-10 max-w-2xl mx-auto">
              Agende uma avaliação médica e entenda se o meu acompanhamento faz sentido para o seu caso.
            </p>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agendamento-hero-cta"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica agora
            </a>
          </div>
        </div>
      </section>

      {/* O que acontece na primeira consulta */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 
                className="section-title mb-8"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                O que acontece na sua primeira consulta.
              </h2>
              
              <p className="text-lg text-[#666666] leading-relaxed mb-6">
                A primeira consulta não é apenas uma conversa rápida.
              </p>
              
              <p className="text-lg text-[#2A2A2A] font-medium mb-8">
                Ela é uma avaliação médica aprofundada para entender se — e como — eu posso te ajudar.
              </p>
              
              <ul className="space-y-3">
                {[
                  'Anamnese detalhada',
                  'Avaliação de sintomas e queixas',
                  'Histórico clínico completo',
                  'Análise de tentativas anteriores',
                  'Definição de objetivos realistas',
                  'Solicitação de exames (se necessário)',
                  'Orientações iniciais',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-[#B8944D]" />
                    <span className="text-[#666666]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-[#FAF9F7] p-8 md:p-12 rounded-md">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#B8944D]/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-[#B8944D]" />
                </div>
                <div>
                  <h3 className="text-xl text-[#2A2A2A]" style={{ fontFamily: 'var(--font-heading)' }}>
                    Agende via WhatsApp
                  </h3>
                  <p className="text-sm text-[#666666]">Atendimento rápido e direto</p>
                </div>
              </div>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#B8944D] flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#2A2A2A] font-medium">Endereço</p>
                    <p className="text-[#666666] text-sm">
                      R. André Ferreira Barbosa, 8157<br />
                      Pinheirinho, Curitiba - PR<br />
                      CEP: 81150-040
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#B8944D] flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-[#2A2A2A] font-medium">WhatsApp</p>
                    <p className="text-[#666666] text-sm">(41) 98845-1399</p>
                  </div>
                </div>
              </div>
              
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="agendamento-card-cta"
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Agendar agora
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Para quem é / Para quem não é */}
      <section className="section-padding bg-[#E5E2DD]/30">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Para quem é */}
            <div>
              <h2 
                className="text-2xl text-[#2A2A2A] mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Para quem essa avaliação é indicada.
              </h2>
              
              <ul className="space-y-3">
                {[
                  'Para quem quer emagrecer com acompanhamento médico',
                  'Para quem suspeita de desequilíbrio hormonal',
                  'Para quem já tentou de tudo',
                  'Para quem busca tratamento sério',
                  'Para quem quer segurança e resultado sustentável',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#B8944D] flex-shrink-0 mt-0.5" />
                    <span className="text-[#666666]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Para quem não é */}
            <div>
              <h2 
                className="text-2xl text-[#2A2A2A] mb-6"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Para quem essa avaliação não é indicada.
              </h2>
              
              <ul className="space-y-3 text-[#888888]">
                {[
                  'Para quem quer resultado rápido',
                  'Para quem busca hormônios por estética',
                  'Para quem quer protocolo pronto',
                  'Para quem não quer acompanhamento',
                  'Para quem quer promessas irreais',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-[#888888]">✕</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* O que você vai receber */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-8 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O que você vai receber ao agendar sua avaliação.
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                'Avaliação médica aprofundada',
                'Análise individual do seu caso',
                'Solicitação de exames (se indicado)',
                'Direcionamento terapêutico inicial',
                'Clareza sobre próximos passos',
                'Transparência total',
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-[#FAF9F7] p-4 rounded-md">
                  <Check className="w-5 h-5 text-[#B8944D]" />
                  <span className="text-[#2A2A2A]">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Segurança e ética */}
      <section className="section-padding bg-[#FAF9F7]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="section-title mb-8"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Segurança e ética médica.
            </h2>
            
            <div className="space-y-4 text-lg text-[#666666] leading-relaxed">
              <p>Nenhum tratamento é iniciado sem avaliação adequada.</p>
              <p>Nenhuma prescrição é feita sem exames quando necessários.</p>
              <p>Nenhuma promessa é feita sem base médica.</p>
              <p className="text-[#2A2A2A] font-medium pt-4">
                Meu compromisso é com sua saúde — não com atalhos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Próximos passos */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 
              className="section-title mb-12 text-center"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              O que acontece depois que você agenda.
            </h2>
            
            <div className="space-y-6">
              {[
                'Você agenda sua avaliação',
                'Recebe a confirmação',
                'Comparece à consulta',
                'Realiza exames (se solicitados)',
                'Retorna para definição da estratégia',
                'Inicia o tratamento (se indicado)',
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-6">
                  <div className="step-number flex-shrink-0">{index + 1}</div>
                  <div className="flex-grow flex items-center justify-between">
                    <span className="text-[#2A2A2A]">{step}</span>
                    {index < 5 && <ArrowRight className="w-4 h-4 text-[#E5E2DD] hidden md:block" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-[#2A2A2A] text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl text-white mb-6 leading-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Se você quer um tratamento médico sério,{' '}
              <span className="text-[#B8944D]">este é o seu próximo passo</span>.
            </h2>
            
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="agendamento-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg mt-8"
            >
              <Phone className="w-5 h-5" />
              Agendar avaliação médica agora
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};
